import { ColumnDef } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { BoostListingButton } from 'src/components/SponsoredListings/BoostListingButton';
import { UpdateCampaignStateButton } from 'src/components/SponsoredListings/UpdateCampaignStateButton';
import { BaseCell } from 'src/components/TableV2/cells';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import * as styles from 'src/tables/SponsoredListings/SponsoredListingsTable.css';
import { TableShimmeringDiv } from 'src/tables/Table';
import { MOCK_SPONSORED_LISTINGS_DATA } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  applyNumberFormatting,
  formatPercent,
} from 'src/utils/numberFormatter';
import {
  AdCampaignState,
  SponsoredListingsCampaign,
  TicketClassInfo,
} from 'src/WebApiController';

export const getTableColumnDefinitions = (
  onStartWork: () => void,
  onStopWork: (success: boolean) => void,
  disabled?: boolean
): ColumnDef<SponsoredListingsCampaign>[] => [
  {
    id: 'campaign',
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.CampaignName} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.campaignName,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue<string>();
      return <BaseCell align="left">{value}</BaseCell>;
    },
  },
  {
    id: 'startDate',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.StartDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.dateRange?.start,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue() as string;
      const dateValue = value ? new Date(value).toISOString() : value;

      return (
        <BaseCell align="right">
          {formatInTimeZone(
            dateValue ? new Date(dateValue) : new Date(),
            'UTC',
            'M/d/yyyy h:mm aa'
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'endDate',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.EndDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.dateRange?.end,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue<string>();
      const dateValue = value ? new Date(value).toISOString() : value;

      return (
        <BaseCell align="right">
          {formatInTimeZone(
            dateValue ? new Date(dateValue) : new Date(),
            'UTC',
            'M/d/yyyy h:mm aa'
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'revenue',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Revenue} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      const { getUiCurrency } = useLocalizationContext();
      const { campaignSalesAggregates } = useAdCampaignsDataContext();

      if (!original || !campaignSalesAggregates) {
        return <TableShimmeringDiv />;
      }

      const salesData = campaignSalesAggregates[original.campaignId];

      const currency = getUiCurrency(salesData.currencyCode || 'USD');

      let revenue = salesData?.totalNetProceeds;

      // In development, assign random revenue if the actual value is 0
      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $100 and $10,000
        revenue = Math.floor(Math.random() * 9900) + 100;
      }

      return (
        <BaseCell align="right" style={{ padding: vars.spacing.xs }}>
          {revenue && (
            <div
              className={revenue > 0 ? styles.revenuePositiveCell : undefined}
            >
              {applyNumberFormatting({
                inputNumber: revenue,
                currencyCode: currency.code,
                currencyDecimalDigits: currency.dec,
              })}
            </div>
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'spend',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.AdSpend} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.totalSpend,
    cell: function Cell({ getValue, row: { original } }) {
      const { getUiCurrency } = useLocalizationContext();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const spend = getValue<number | null>();

      const currency = getUiCurrency(original.currencyCode || 'USD');

      return (
        <BaseCell align="right">
          {spend &&
            applyNumberFormatting({
              inputNumber: spend,
              currencyCode: currency.code,
              currencyDecimalDigits: currency.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'roas',
    header: () => <TableHeaderCellDiv align="right">ROAS</TableHeaderCellDiv>,
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      const { campaignSalesAggregates } = useAdCampaignsDataContext();

      if (!original || !campaignSalesAggregates) {
        return <TableShimmeringDiv />;
      }

      const salesData = campaignSalesAggregates[original.campaignId];

      let revenue = salesData?.totalNetProceeds;

      // In development, assign random revenue if the actual value is 0
      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $1 and budget * 10
        revenue =
          Math.floor(Math.random() * ((original.totalBudget ?? 1) * 10 - 1)) +
          1;
      }

      const roas = original?.totalSpend ? revenue / original.totalSpend : 0;
      return (
        <BaseCell align="right">{roas && formatPercent(roas, 2)}</BaseCell>
      );
    },
  },
  {
    id: 'sales',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Sales} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      const { campaignSalesAggregates } = useAdCampaignsDataContext();

      if (!original || !campaignSalesAggregates) {
        return <TableShimmeringDiv />;
      }

      const salesData = campaignSalesAggregates[original.campaignId];

      return salesData ? (
        <BaseCell align="right">{salesData.saleCount}</BaseCell>
      ) : null;
    },
  },
  {
    id: 'budget',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Budget} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.totalBudget,
    cell: function Cell({ getValue, row: { original } }) {
      const { getUiCurrency } = useLocalizationContext();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const currency = getUiCurrency(original.currencyCode || 'USD');
      const budget = getValue() as number | null;

      return (
        <BaseCell align="right">
          {applyNumberFormatting({
            inputNumber: budget,
            currencyCode: currency.code,
            currencyDecimalDigits: currency.dec,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'left',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Remaining} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      const { getUiCurrency } = useLocalizationContext();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const currency = getUiCurrency(original.currencyCode || 'USD');
      // Calculate budget left
      const budget = original.totalBudget;
      const spend = original.totalSpend;

      const remaining = (budget ?? 0) - (spend ?? 0);

      return (
        <BaseCell align="right">
          {applyNumberFormatting({
            inputNumber: remaining,
            currencyCode: currency.code,
            currencyDecimalDigits: currency.dec,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'target',
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Target} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.targetTicketClassIds,
    cell: function Cell({ row: { original } }) {
      const { venueMapInfo } = useEventMapContext();

      // Extract all TicketClassInfo from venueMapInfo as a record keyed by ticketClassId
      const ticketClassesById = useMemo(() => {
        if (!venueMapInfo) return {};

        return venueMapInfo.sections
          .flatMap((s) => s.rows.map((r) => r.tktClass))
          .reduce(
            (record, ticketClass) => {
              if (ticketClass?.id) {
                record[ticketClass.id] = ticketClass;
              }
              return record;
            },
            {} as Record<number, TicketClassInfo>
          );
      }, [venueMapInfo]);

      if (!original) {
        return <TableShimmeringDiv />;
      }

      // Display target information
      let target = 'All Zones';
      if (
        original.targetTicketClassIds?.length &&
        Object.keys(ticketClassesById).length > 0
      ) {
        const targetedClasses = original.targetTicketClassIds
          .map((id) => ticketClassesById[id]?.name)
          .filter(Boolean);

        if (targetedClasses.length > 0) {
          target = targetedClasses.join(', ');
        }
      }

      return <BaseCell align="left">{target}</BaseCell>;
    },
  },
  {
    id: 'actions',
    header: () => (
      <TableHeaderCellDiv align="center">
        <Content id={ContentId.Actions} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      return (
        <BaseCell
          align="center"
          style={{ padding: vars.spacing.sm, paddingRight: vars.spacing.m }}
        >
          <Stack gap="m" alignItems="center" justifyContent="center">
            <UpdateCampaignStateButton
              iconOnly
              campaign={original}
              state={
                original.campaignState === AdCampaignState.Active
                  ? AdCampaignState.Paused
                  : AdCampaignState.Active
              }
              onBeforePause={onStartWork}
              onAfterPause={onStopWork}
              disabled={disabled}
            />
            <BoostListingButton
              campaign={original}
              iconOnly
              disabled={disabled}
            />
          </Stack>
        </BaseCell>
      );
    },
  },
];
